import React, { useState, useEffect, useCallback } from 'react';
import { fetchSummary, fetchPrograms, fetchMappings } from './adrlHealthApi';

const STATUS_COLORS = {
  healthy: { bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
  degraded: { bg: 'bg-yellow-100', text: 'text-yellow-800', dot: 'bg-yellow-500' },
  broken: { bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' },
};

const CONFIDENCE_LABELS = {
  1.0: { label: 'Exact', color: 'text-green-700 bg-green-50' },
  0.8: { label: 'High', color: 'text-blue-700 bg-blue-50' },
  0.5: { label: 'Medium', color: 'text-yellow-700 bg-yellow-50' },
  0.2: { label: 'Low', color: 'text-red-700 bg-red-50' },
};

function getConfidenceInfo(confidence) {
  if (confidence >= 1.0) return CONFIDENCE_LABELS[1.0];
  if (confidence >= 0.8) return CONFIDENCE_LABELS[0.8];
  if (confidence >= 0.5) return CONFIDENCE_LABELS[0.5];
  return CONFIDENCE_LABELS[0.2];
}

// ─── Summary Cards ────────────────────────────────────────────────

const SummaryCards = ({ summary }) => {
  if (!summary) return null;
  const { total_programs, healthy, degraded, broken, total_missing_rigid, total_missing_choose } = summary;
  const healthyPct = total_programs ? Math.round((healthy / total_programs) * 100) : 0;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Card label="Total Programs" value={total_programs} sub={`${healthyPct}% healthy`} />
      <Card label="Healthy" value={healthy} color="text-green-600" />
      <Card label="Degraded" value={degraded} color="text-yellow-600"
            sub={`${total_missing_choose} choose-block gaps`} />
      <Card label="Broken" value={broken} color="text-red-600"
            sub={`${total_missing_rigid} missing rigid codes`} />
    </div>
  );
};

const Card = ({ label, value, color = 'text-gray-900', sub }) => (
  <div className="bg-white rounded-lg border p-4">
    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${color}`}>{value}</div>
    {sub && <div className="text-xs text-gray-400 mt-1">{sub}</div>}
  </div>
);

// ─── College Breakdown ────────────────────────────────────────────

const CollegeBreakdown = ({ breakdown }) => {
  if (!breakdown || Object.keys(breakdown).length === 0) return null;

  const sorted = Object.entries(breakdown).sort((a, b) => {
    const totalA = a[1].healthy + a[1].degraded + a[1].broken;
    const totalB = b[1].healthy + b[1].degraded + b[1].broken;
    return totalB - totalA;
  });

  return (
    <div className="bg-white rounded-lg border p-4">
      <h3 className="text-sm font-semibold text-gray-700 mb-3">Health by College</h3>
      <div className="space-y-2">
        {sorted.map(([college, stats]) => {
          const total = stats.healthy + stats.degraded + stats.broken;
          return (
            <div key={college} className="flex items-center gap-3 text-sm">
              <span className="flex-1 text-gray-700 truncate" title={college}>{college}</span>
              <div className="flex gap-1">
                <BarSegment count={stats.healthy} total={total} color="bg-green-400" />
                <BarSegment count={stats.degraded} total={total} color="bg-yellow-400" />
                <BarSegment count={stats.broken} total={total} color="bg-red-400" />
              </div>
              <span className="text-xs text-gray-400 w-12 text-right">{total}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const BarSegment = ({ count, total, color }) => {
  const width = total ? Math.max((count / total) * 120, count > 0 ? 4 : 0) : 0;
  return <div className={`h-4 rounded-sm ${color}`} style={{ width: `${width}px` }} title={count} />;
};

// ─── Program List ─────────────────────────────────────────────────

const ProgramList = ({ programs, onSelectProgram }) => {
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filtered = programs.filter(p => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (filter) {
      const q = filter.toLowerCase();
      return (
        p.program_name.toLowerCase().includes(q) ||
        p.department_code?.toLowerCase().includes(q) ||
        p.rigid_missing.some(c => c.toLowerCase().includes(q)) ||
        p.choose_missing.some(c => c.toLowerCase().includes(q))
      );
    }
    return true;
  });

  return (
    <div className="bg-white rounded-lg border">
      <div className="p-4 border-b flex items-center gap-3 flex-wrap">
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Search programs or course codes..."
          className="border border-gray-300 rounded px-3 py-1.5 text-sm flex-1 min-w-[200px]"
        />
        <div className="flex gap-1">
          {['all', 'broken', 'degraded', 'healthy'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1 rounded text-xs font-medium ${
                statusFilter === s
                  ? 'bg-gray-800 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-400">{filtered.length} programs</span>
      </div>

      <div className="divide-y max-h-[600px] overflow-y-auto">
        {filtered.map(p => (
          <ProgramRow key={p.adrl_file_id} program={p} onSelect={onSelectProgram} />
        ))}
        {filtered.length === 0 && (
          <div className="p-8 text-center text-sm text-gray-400">No programs match the filter.</div>
        )}
      </div>
    </div>
  );
};

const ProgramRow = ({ program: p, onSelect }) => {
  const sc = STATUS_COLORS[p.status];
  return (
    <button
      onClick={() => onSelect(p)}
      className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-start gap-3"
    >
      <span className={`mt-1 inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 ${sc.dot}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900 truncate">{p.program_name}</span>
          <span className={`text-xs px-1.5 py-0.5 rounded ${sc.bg} ${sc.text}`}>{p.status}</span>
          {p.department_code && (
            <span className="text-xs text-gray-400">{p.department_code}</span>
          )}
        </div>
        {(p.rigid_missing_count > 0 || p.choose_missing_count > 0) && (
          <div className="mt-1 flex gap-4 text-xs text-gray-500">
            {p.rigid_missing_count > 0 && (
              <span className="text-red-600">
                {p.rigid_missing_count} rigid missing: {p.rigid_missing.slice(0, 5).join(', ')}
                {p.rigid_missing.length > 5 ? ` +${p.rigid_missing.length - 5} more` : ''}
              </span>
            )}
            {p.choose_missing_count > 0 && (
              <span className="text-yellow-600">
                {p.choose_missing_count} choose gaps
              </span>
            )}
          </div>
        )}
      </div>
    </button>
  );
};

// ─── Mapping Detail Panel ─────────────────────────────────────────

const MappingPanel = ({ program, mappings, onClose }) => {
  if (!program) return null;

  const programMappings = mappings.filter(m => m.adrl_file_id === program.adrl_file_id);

  return (
    <div className="bg-white rounded-lg border">
      <div className="p-4 border-b flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{program.program_name}</h3>
          <p className="text-xs text-gray-400 mt-0.5">
            {program.file_path} — {program.rigid_missing_count} missing rigid codes
          </p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg px-2">
          &times;
        </button>
      </div>

      {programMappings.length === 0 ? (
        <div className="p-6 text-center text-sm text-gray-400">No mappings to show.</div>
      ) : (
        <div className="divide-y">
          {programMappings.map((m, i) => (
            <MappingRow key={i} mapping={m} />
          ))}
        </div>
      )}
    </div>
  );
};

const MappingRow = ({ mapping: m }) => {
  const [expanded, setExpanded] = useState(false);
  const conf = getConfidenceInfo(m.confidence);

  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 text-sm">
            <code className="text-red-600 bg-red-50 px-1.5 py-0.5 rounded font-mono text-xs">
              {m.missing_code}
            </code>
            {m.suggested_code ? (
              <>
                <span className="text-gray-400">&rarr;</span>
                <code className="text-green-700 bg-green-50 px-1.5 py-0.5 rounded font-mono text-xs">
                  {m.suggested_code}
                </code>
                <span className="text-xs text-gray-500 truncate">{m.suggested_title}</span>
                {m.suggested_credits && (
                  <span className="text-xs text-gray-400">({m.suggested_credits} cr)</span>
                )}
              </>
            ) : (
              <span className="text-xs text-gray-400 italic">No suggestion found</span>
            )}
          </div>
        </div>

        <span className={`text-xs px-1.5 py-0.5 rounded ${conf.color}`}>{conf.label}</span>

        {m.all_candidates?.length > 1 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            {expanded ? 'Hide' : `${m.all_candidates.length} options`}
          </button>
        )}
      </div>

      {expanded && m.all_candidates && (
        <div className="mt-2 ml-6 border-l-2 border-gray-100 pl-3 space-y-1">
          {m.all_candidates.map((c, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-gray-500">
              <code className="font-mono">{c.code}</code>
              <span className="truncate">{c.title}</span>
              <span className="text-gray-300">({c.credits} cr, dist {c.distance})</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────

const ADRLHealth = () => {
  const [summary, setSummary] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [summaryData, programsData, mappingsData] = await Promise.all([
        fetchSummary(),
        fetchPrograms(),
        fetchMappings(),
      ]);
      setSummary(summaryData);
      setPrograms(programsData.programs || []);
      setMappings(mappingsData.mappings || []);
    } catch (e) {
      setError(`Failed to load program health data: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSelectProgram = (program) => {
    setSelectedProgram(program);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-6 w-6 border-2 border-blue-600 border-t-transparent rounded-full" />
        <span className="ml-3 text-sm text-gray-500">Scanning program health...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Program Health</h2>
          <p className="text-sm text-gray-500 mt-1">
            Validate program requirements against the course database
          </p>
        </div>
        <button
          onClick={loadData}
          className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {error}
          <button onClick={() => setError(null)}
                  className="ml-2 text-red-500 hover:text-red-700">dismiss</button>
        </div>
      )}

      {/* Summary */}
      <SummaryCards summary={summary} />

      {/* College breakdown */}
      <CollegeBreakdown breakdown={summary?.college_breakdown} />

      {/* Program list + mapping detail */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ProgramList programs={programs} onSelectProgram={handleSelectProgram} />
        {selectedProgram ? (
          <MappingPanel
            program={selectedProgram}
            mappings={mappings}
            onClose={() => setSelectedProgram(null)}
          />
        ) : (
          <div className="bg-white rounded-lg border p-8 flex items-center justify-center text-sm text-gray-400">
            Select a broken program to see mapping suggestions
          </div>
        )}
      </div>
    </div>
  );
};

export default ADRLHealth;
